import { Suspense, lazy, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { SignedIn, SignedOut } from "@clerk/clerk-react";
import Intercom from "@intercom/messenger-js-sdk";
import ls from "@livesession/browser";
import { useGetCurrentUserQuery } from "./api/users";
import { useGetUserAttorneyQuery } from "./api/attorneys";
import type { Attorney } from "../../api/src/types";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { PageLoading } from "./components/Loading";
import ErrorMessage, { PageErrorContainer } from "./components/Error";
import { formatUserFullName } from "./utils/format";
import config from "./config";
import Negotiations from "./pages/negotiations/Negotiations";
import NavLayout from "./components/layout/NavLayout";
import AdminNavLayout from "./components/layout/admins/AdminNavLayout";
import AttorneyNavLayout from "./components/layout/attorneys/AttorneyNavLayout";
import LoadToError from "./LoadToError";
import { RudderStackTracker } from "./components/RudderStackTracker";
import { identifyUser, trackEvent, analytics } from "./utils/rudderstack";

// Shared components (loaded immediately for all user types)
const SigninRedirect = lazy(() => import("./pages/auth/SigninRedirect"));
const PostSigninRedirect = lazy(
  () => import("./pages/auth/PostSigninRedirect"),
);

// User-specific components (code-split by user type)
const Home = lazy(() => import("./pages/home/Home"));
const Profile = lazy(() => import("./pages/profile/Profile"));
const QuestionnaireRedirect = lazy(
  () => import("./pages/questionnaire/QuestionnaireRedirect"),
);
const Financials = lazy(() => import("./pages/financials/Financials"));
const DocumentsPage = lazy(() => import("./pages/documents/DocumentsPage"));
const DocumentsReviewPage = lazy(
  () => import("./pages/documents/review/DocumentsReviewPage"),
);
const NotarizationPage = lazy(
  () => import("./pages/notarization/NotarizationPage"),
);
const NotarizeReviewPage = lazy(
  () => import("./pages/notarization/review/NotarizeReviewPage"),
);
const Congratulations = lazy(
  () => import("./pages/notarization/Congratulations"),
);
const Payment = lazy(() => import("./pages/payment/Payment"));
const PaymentSuccess = lazy(() => import("./pages/payment/PaymentSuccess"));
const AttorneyServices = lazy(
  () => import("./pages/attorney-services/AttorneyServices"),
);
const PostConversionStore = lazy(
  () => import("./pages/payment/PostConversionStore"),
);

// Attorney-specific components (separate chunk)
const AttorneyForm = lazy(
  () => import("./pages/attorney/application-form/AttorneyForm"),
);
const AttorneyAccount = lazy(
  () => import("./pages/attorney/profile/AttorneyAccount"),
);
const ClientsListPage = lazy(
  () => import("./pages/attorney/clients/ClientsListPage"),
);
const AttorneyReviews = lazy(
  () => import("./pages/attorney/reviews/AttorneyReviews"),
);
const AttorneyAppointments = lazy(
  () => import("./pages/attorney/appointments/AttorneyAppointments"),
);
const AttorneyDashboard = lazy(
  () => import("./pages/attorney/dashboard/AttorneyDashboard"),
);
const ClientProfile = lazy(
  () => import("./pages/attorney/clients/ClientProfile"),
);
const AttorneyPayouts = lazy(
  () => import("./pages/attorney/payouts/AttorneyPayouts"),
);
const EditorPage = lazy(() => import("./pages/documents/EditorPage"));
const StripeOnboardingRefreshPage = lazy(
  () =>
    import("./pages/attorney/stripe-onboarding/StripeOnboardingRefreshPage"),
);

// Auth components (separate chunk)
const Login = lazy(() => import("./pages/auth/Signin"));
const Signup = lazy(() => import("./pages/auth/Signup"));
const OAuthCallback = lazy(() => import("./pages/auth/OAuthCallback"));

// Admin components (separate chunk)
const AdminAttorneyPayouts = lazy(
  () => import("./pages/admin/attorney-payouts/AttorneyPayoutsPage"),
);
const StatesPage = lazy(() => import("./pages/admin/states/StatesListPage"));
const StateEditPage = lazy(() => import("./pages/admin/states/StatePage"));
const AttorneyOrderPage = lazy(
  () => import("./pages/admin/states/AttorneyOrderPage"),
);
const UsersPage = lazy(() => import("./pages/admin/users/UsersListPage"));
const UserPage = lazy(() => import("./pages/admin/users/UserPage"));
const AttorneysListPage = lazy(
  () => import("./pages/admin/attorneys/AttorneysListPage"),
);
const SingleAttorneyPage = lazy(
  () => import("./pages/admin/attorneys/SingleAttorneyPage"),
);
const LibraryGroupsListPage = lazy(
  () => import("./pages/admin/library-groups/LibraryGroupsListPage"),
);
const LibraryGroupEditPage = lazy(
  () => import("./pages/admin/library-groups/LibraryGroupPage"),
);
const LawFirmListPage = lazy(
  () => import("./pages/admin/law-firms/LawFirmListPage"),
);
const SingleLawFirmPage = lazy(
  () => import("./pages/admin/law-firms/SingleLawFirmPage"),
);
const AppointmentList = lazy(
  () => import("./pages/admin/attorney-appointments/AppointmentList"),
);

const AttorneyClientList = lazy(
  () => import("./pages/admin/attorney-clients/AttorneyClientList"),
);

const DiscountsListPage = lazy(
  () => import("./pages/admin/discounts/DiscountsListPage"),
);

const AttorneyReviewsList = lazy(
  () => import("./pages/admin/attorney-reviews/AttorneyReviewsList"),
);

// Route component wrappers with Suspense
function SuspenseRoute({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoading />}>{children}</Suspense>;
}

function userRoutes() {
  return (
    <NavLayout>
      <Routes>
        <Route
          path="/signin-redirect"
          element={
            <SuspenseRoute>
              <PostSigninRedirect />
            </SuspenseRoute>
          }
        />
        <Route
          path="/"
          element={
            <SuspenseRoute>
              <Home />
            </SuspenseRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <SuspenseRoute>
              <Profile />
            </SuspenseRoute>
          }
        />
        <Route
          path="/questionnaire"
          element={
            <SuspenseRoute>
              <QuestionnaireRedirect />
            </SuspenseRoute>
          }
        />
        <Route
          path="/questionnaire/:section"
          element={
            <SuspenseRoute>
              <QuestionnaireRedirect />
            </SuspenseRoute>
          }
        />
        <Route
          path="/alignment"
          element={
            <SuspenseRoute>
              <Negotiations />
            </SuspenseRoute>
          }
        />
        <Route
          path="/financial-disclosure"
          element={
            <SuspenseRoute>
              <Financials />
            </SuspenseRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <SuspenseRoute>
              <DocumentsPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/documents/:documentId/editor"
          element={
            <SuspenseRoute>
              <EditorPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/documents/review"
          element={
            <SuspenseRoute>
              <DocumentsReviewPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/notarize"
          element={
            <SuspenseRoute>
              <NotarizationPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/notarize/review"
          element={
            <SuspenseRoute>
              <NotarizeReviewPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/congratulations"
          element={
            <SuspenseRoute>
              <Congratulations />
            </SuspenseRoute>
          }
        />
        <Route
          path="/payment"
          element={
            <SuspenseRoute>
              <Payment />
            </SuspenseRoute>
          }
        />
        <Route
          path="/payment/success"
          element={
            <SuspenseRoute>
              <PaymentSuccess />
            </SuspenseRoute>
          }
        />
        <Route
          path="/payment/history"
          element={
            <SuspenseRoute>
              <PostConversionStore />
            </SuspenseRoute>
          }
        />
        <Route
          path="/attorney-services"
          element={
            <SuspenseRoute>
              <AttorneyServices />
            </SuspenseRoute>
          }
        />
        {/* Routes from old app still in use by the emails. Once the emails are updated, these routes can be removed. */}
        <Route
          path="/user_ask_an_attorney"
          element={<Navigate to="/attorney-services?serviceType=Q_A" />}
        />
        <Route path="/user_documents" element={<Navigate to="/documents" />} />
        <Route
          path="/user_attorney_booking"
          element={
            <Navigate to="/attorney-services?serviceType=REPRESENTATION" />
          }
        />
        <Route
          path="/attorney_editor/:fileId"
          element={<Navigate to="/documents" />}
        />
        <Route
          path="/initial_questionnaire/:section"
          element={<Navigate to="/questionnaire" />}
        />
        <Route path="/user_account" element={<Navigate to="/" />} />
        <Route
          path="/user_notarization"
          element={<Navigate to="/notarize" />}
        />
        <Route path="/user_settings" element={<Navigate to="/profile" />} />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </NavLayout>
  );
}

function attorneyRoutes(attorney: Attorney) {
  if (attorney.stage === "FORM_MISSING" || attorney.stage === "UNDER_REVIEW") {
    return (
      <AttorneyNavLayout>
        <Routes>
          <Route
            path="/attorney/form"
            element={
              <SuspenseRoute>
                <AttorneyForm />
              </SuspenseRoute>
            }
          />
          <Route path="*" element={<Navigate to="/attorney/form" />} />
        </Routes>
      </AttorneyNavLayout>
    );
  } else if (attorney.stage === "APPROVED") {
    return (
      <AttorneyNavLayout>
        <Routes>
          <Route
            path="/attorney/account"
            element={
              <SuspenseRoute>
                <AttorneyAccount />
              </SuspenseRoute>
            }
          />
          <Route
            path="/attorney/clients"
            element={
              <SuspenseRoute>
                <ClientsListPage />
              </SuspenseRoute>
            }
          />
          <Route
            path="/attorney/reviews"
            element={
              <SuspenseRoute>
                <AttorneyReviews />
              </SuspenseRoute>
            }
          />
          <Route
            path="/attorney/appointments"
            element={
              <SuspenseRoute>
                <AttorneyAppointments />
              </SuspenseRoute>
            }
          />
          <Route
            path="/attorney/clients/:clientId"
            element={
              <SuspenseRoute>
                <ClientProfile />
              </SuspenseRoute>
            }
          />
          <Route
            path="/"
            element={
              <SuspenseRoute>
                <AttorneyDashboard />
              </SuspenseRoute>
            }
          />
          <Route
            path="/attorney/payouts"
            element={
              <SuspenseRoute>
                <AttorneyPayouts />
              </SuspenseRoute>
            }
          />
          <Route
            path="/documents/:documentId/editor"
            element={
              <SuspenseRoute>
                <EditorPage />
              </SuspenseRoute>
            }
          />
          <Route
            path="/attorney/stripe-onboarding-refresh"
            element={
              <SuspenseRoute>
                <StripeOnboardingRefreshPage />
              </SuspenseRoute>
            }
          />
          {/* Routes from old app still in use by the emails. Once the emails are updated, these routes can be removed. */}
          <Route
            path="/attorney_editor/:fileId"
            element={<Navigate to="/attorney/clients" />}
          />
          <Route
            path="/attorney_clients"
            element={<Navigate to="/attorney/clients" />}
          />
          <Route
            path="/attorney_profile"
            element={<Navigate to="/attorney/account" />}
          />

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </AttorneyNavLayout>
    );
  }

  return (
    <AttorneyNavLayout>
      <Routes>
        <Route
          path="/attorney/account"
          element={
            <PageErrorContainer>
              <ErrorMessage>
                Your application has been declined or deactivated. Please
                contact support if you have any questions.
              </ErrorMessage>
            </PageErrorContainer>
          }
        />
        <Route path="*" element={<Navigate to="/attorney/account" />} />
      </Routes>
    </AttorneyNavLayout>
  );
}

function adminRoutes() {
  return (
    <AdminNavLayout>
      <Routes>
        <Route
          path="/admin/attorney/payouts"
          element={
            <SuspenseRoute>
              <AdminAttorneyPayouts />
            </SuspenseRoute>
          }
        />
        <Route
          path="/admin/states"
          element={
            <SuspenseRoute>
              <StatesPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/admin/states/:id"
          element={
            <SuspenseRoute>
              <StateEditPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/admin/states/:id/attorney-order"
          element={
            <SuspenseRoute>
              <AttorneyOrderPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <SuspenseRoute>
              <UsersPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/admin/users/:id"
          element={
            <SuspenseRoute>
              <UserPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/admin/attorneys"
          element={
            <SuspenseRoute>
              <AttorneysListPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/admin/attorneys/:attorneyId"
          element={
            <SuspenseRoute>
              <SingleAttorneyPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/admin/attorneys/:attorneyId/clients"
          element={
            <SuspenseRoute>
              <AttorneyClientList />
            </SuspenseRoute>
          }
        />
        <Route
          path="/admin/library-groups"
          element={
            <SuspenseRoute>
              <LibraryGroupsListPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/admin/library-groups/:id"
          element={
            <SuspenseRoute>
              <LibraryGroupEditPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/admin/law-firms"
          element={
            <SuspenseRoute>
              <LawFirmListPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/admin/law-firms/:lawFirmId"
          element={
            <SuspenseRoute>
              <SingleLawFirmPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/admin/attorney-appointments"
          element={
            <SuspenseRoute>
              <AppointmentList />
            </SuspenseRoute>
          }
        />
        <Route
          path="/admin/discounts"
          element={
            <SuspenseRoute>
              <DiscountsListPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/documents/:documentId/editor"
          element={
            <SuspenseRoute>
              <EditorPage />
            </SuspenseRoute>
          }
        />
        <Route
          path="/admin/attorney-reviews"
          element={
            <SuspenseRoute>
              <AttorneyReviewsList />
            </SuspenseRoute>
          }
        />
        <Route path="*" element={<Navigate to="/admin/users" />} />
      </Routes>
    </AdminNavLayout>
  );
}

function App() {
  const {
    data: user,
    isLoading: isUserLoading,
    isFetching: isUserFetching,
  } = useGetCurrentUserQuery();
  const isUserAttorney = user?.userType === "ATTORNEY";
  const isAdmin = user?.userType === "ADMIN";
  const isCustomer = user?.userType === "CUSTOMER";
  const { data: attorney, isLoading: isAttorneyLoading } =
    useGetUserAttorneyQuery(user && isUserAttorney ? user.id : skipToken);

  const lastIdentifiedUserId = useRef<string | null>(null);

  useEffect(() => {
    if (user) {
      Intercom({
        app_id: config.intercomAppId,
        user_id: user.id,
        name: formatUserFullName(user, "full"),
        email: user.email,
        created_at: new Date(user.createdAt).getTime() / 1000,
      });

      if (config.environment !== "development") {
        ls.identify({
          name: formatUserFullName(user, "full"),
          email: user.email,
        });
      }

      if (lastIdentifiedUserId.current !== user.id) {
        identifyUser(user.id, {
          email: user.email,
          user_type: user.userType,
          created_at: user.createdAt,
        });
        trackEvent("user_identified", {
          user_id: user.id,
          user_type: user.userType,
        });
        lastIdentifiedUserId.current = user.id;
      }
    } else {
      if (lastIdentifiedUserId.current) {
        analytics.reset();
        lastIdentifiedUserId.current = null;
      }
    }
  }, [user]);

  if (isUserLoading || isAttorneyLoading) {
    return <PageLoading />;
  }

  if (isUserAttorney && !attorney) {
    return (
      <PageErrorContainer>
        <ErrorMessage>No attorney found</ErrorMessage>
      </PageErrorContainer>
    );
  }

  return (
    <BrowserRouter>
      <RudderStackTracker />
      <SignedIn>
        {isAdmin ? (
          adminRoutes()
        ) : isUserAttorney && attorney ? (
          attorneyRoutes(attorney)
        ) : isCustomer ? (
          userRoutes()
        ) : (
          <Routes>
            <Route
              path="/oauth/callback"
              element={
                <PageErrorContainer>
                  <SuspenseRoute>
                    <OAuthCallback />
                  </SuspenseRoute>
                </PageErrorContainer>
              }
            />
            <Route
              path="*"
              element={<LoadToError isFetching={isUserFetching} />}
            />
          </Routes>
        )}
      </SignedIn>
      <SignedOut>
        <Routes>
          <Route
            path="/login"
            element={
              <SuspenseRoute>
                <Login />
              </SuspenseRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <SuspenseRoute>
                <Signup />
              </SuspenseRoute>
            }
          />
          <Route
            path="*"
            element={
              <SuspenseRoute>
                <SigninRedirect />
              </SuspenseRoute>
            }
          />
        </Routes>
      </SignedOut>
    </BrowserRouter>
  );
}

export default App;
